/** A font to await: a CSS font shorthand, optionally paired with the glyph(s)
 *  it will render. The text is essential for unicode-range-subsetted faces
 *  (e.g. Google's Noto Sans Runic) — `document.fonts.load` only fetches the
 *  subset covering the requested characters. */
export type FontSpec = string | { font: string; text?: string }

/**
 * Resolves once the given font specs are loaded, so canvas engines that
 * sample glyphs do not draw tofu/missing-glyph frames before the face is
 * ready. Falls back to resolving immediately where the Font Loading API is
 * unavailable.
 *
 * @param specs CSS font shorthands, optionally each paired with the glyph(s)
 *   it draws, e.g. `{ font: "64px 'Noto Sans Runic'", text: 'ᛗ' }`.
 */
export async function fontReady(specs: FontSpec[]): Promise<void> {
  if (typeof document === 'undefined' || !('fonts' in document)) return
  try {
    await Promise.all(
      specs.map((spec) =>
        typeof spec === 'string'
          ? document.fonts.load(spec)
          : document.fonts.load(spec.font, spec.text),
      ),
    )
    await document.fonts.ready
  } catch {
    // Best-effort: never block rendering on font loading failure.
  }
}

/** True when the user has requested reduced motion. SSR-safe. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}
